import type { CollectionDto } from "@serophin/nottldr-shared";
import { useCollections } from "@serophin/nottldr-shared/hooks";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import { Modal } from "../Modal";
import { CollectionSelect, ModalButtons } from "./CollectionSelect";
import type { CollectionModalProps } from "./CopyCollectionModal";
import { articleCount, errorMessage } from "./collectionText";

const useDelete = (collection: CollectionDto, onClose: () => void) => {
  const toast = useToast();
  const { copy, remove } = useCollections();
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      // The articles are copied first, so a failed copy deletes nothing.
      if (target && collection.articleCount > 0) {
        await copy(collection.id, target);
      }
      await remove(collection.id);
      toast.success("Collection deleted successfully!");
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to delete collection"));
    } finally {
      setBusy(false);
    }
  };

  return { target, setTarget, busy, run };
};

const CopyFirst = (props: { collection: CollectionDto; others: CollectionDto[]; target: string; busy: boolean; onChange: (id: string) => void }) => {
  if (props.collection.articleCount === 0) {
    return null;
  }
  if (props.others.length === 0) {
    return (
      <p class="text-sm text-yellow-600 dark:text-yellow-500">
        Warning: This collection has {articleCount(props.collection.articleCount)} that will be permanently deleted.
      </p>
    );
  }
  return (
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Copy {articleCount(props.collection.articleCount)} to another collection (optional)
      </label>
      <CollectionSelect value={props.target} options={props.others} placeholder="Don't copy articles" disabled={props.busy} onChange={props.onChange} />
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {props.target ? "Articles will be copied before deletion" : "Articles will be permanently deleted"}
      </p>
    </div>
  );
};

export const DeleteCollectionModal = ({ isOpen, onClose, collection, allCollections }: CollectionModalProps) => {
  const { target, setTarget, busy, run } = useDelete(collection, onClose);
  const close = () => {
    setTarget("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <div class="py-2">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Delete Collection</h3>
        <div class="space-y-4">
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Are you sure you want to delete <strong>{collection.name}</strong>?
          </p>
          <CopyFirst collection={collection} others={allCollections.filter((c) => c.id !== collection.id)} target={target} busy={busy} onChange={setTarget} />
          <ModalButtons onCancel={close} onConfirm={run} busy={busy} label="Delete" busyLabel="Deleting..." danger />
        </div>
      </div>
    </Modal>
  );
};
